import {
  getCustomerApiEndpoints,
  getDashboardApiEndpoints,
  getAnalyticsApiEndpoints,
} from './endpoints';
import { getAuth } from './auth';
import { createBackgroundClient } from './background-client';
import type { ApiClient } from './types';

interface AuthConfig {
  accountId: string;
  /** Token can be a static string or a function that returns a fresh token */
  token: string | (() => Promise<string> | string);
}

/**
 * Endpoint URL overrides for region-based routing.
 * When provided, these URLs take precedence over environment variables.
 */
export interface EndpointOverrides {
  customerApiUrl?: string;
  appcuesApiUrl?: string;
  imageServiceLambdaUrl?: string;
  jobApiUrl?: string;
}

/**
 * A function that returns the current JWT token.
 * This allows the API client to be decoupled from token storage mechanisms.
 * Can return a Promise for async storage (e.g., Chrome storage) or a string for sync storage.
 */
export type TokenProvider = () => Promise<string> | string;

/**
 * A function that returns the current account ID.
 * Used for Auth0 tokens where the accountId cannot be extracted from the JWT.
 */
export type AccountIdProvider = () => Promise<string> | string;

/**
 * A function that returns endpoint URL overrides based on the current token.
 * This allows dynamic region-based URL switching.
 */
export type EndpointOverridesProvider = (
  token: string
) => Promise<EndpointOverrides> | EndpointOverrides;

/**
 * Options for the apiClient function
 */
export interface ApiClientOptions {
  /** A function that returns the current JWT token */
  getToken: TokenProvider;
  /**
   * Optional function that returns the account ID.
   * If provided, this is used instead of extracting accountId from the JWT.
   * Required for Auth0 tokens which don't contain accountId in the same format.
   */
  getAccountId?: AccountIdProvider;
  /**
   * Optional function that returns endpoint URL overrides.
   * Called with the current token to enable region-based URL switching.
   * When provided, the returned URLs take precedence over environment variables.
   */
  getEndpointOverrides?: EndpointOverridesProvider;
}

interface CreateApiOptions {
  /** JWT token or TokenProvider callback for authentication */
  jwt: string | TokenProvider;
  /** Account ID (optional - required for extension mode) */
  accountId?: string;
  /** Whether to use standalone mode (direct API calls) vs extension mode (Chrome messaging) */
  standalone: boolean;
  /** Optional endpoint URL overrides for region-based routing */
  endpointOverrides?: EndpointOverrides;
}

const getApiEndpoints = (
  auth: AuthConfig,
  overrides: EndpointOverrides = {}
): ApiClient => {
  // Combine all endpoint collections into the complete API client
  // The individual endpoint functions return Partial<ApiClient>, but together they
  // provide all required methods. The `as` type assertion is safe because:
  // 1. Each endpoint file is responsible for a distinct subset of the API
  // 2. The ApiClient interface documents which methods come from which file
  // 3. Any missing methods will cause runtime errors in tests/usage
  return {
    ...getCustomerApiEndpoints(auth, {
      customerApiUrl: overrides.customerApiUrl,
      imageServiceLambdaUrl: overrides.imageServiceLambdaUrl,
      jobApiUrl: overrides.jobApiUrl,
    }),
    ...getDashboardApiEndpoints(auth, {
      appcuesApiUrl: overrides.appcuesApiUrl,
    }),
    ...getAnalyticsApiEndpoints(auth, {
      customerApiUrl: overrides.customerApiUrl,
    }),
  } as ApiClient;
};

/**
 * Resolves a token from either a static string or a TokenProvider callback.
 */
const resolveToken = async (
  tokenOrProvider: string | TokenProvider
): Promise<string> => {
  if (typeof tokenOrProvider === 'function') {
    return tokenOrProvider();
  }
  return tokenOrProvider;
};

/**
 * Creates the appropriate API client based on the standalone parameter.
 *
 * - In standalone mode: Uses direct API client with the JWT token
 * - In extension mode: Uses background client that proxies requests
 *   through Chrome extension messaging
 *
 * The caller is responsible for determining if they're in standalone mode
 * (typically via `import.meta.env.STANDALONE`).
 *
 * @param options - Configuration options
 * @returns Promise resolving to the ApiClient
 */
export const createApi = async (
  options: CreateApiOptions
): Promise<ApiClient> => {
  const { jwt, accountId, standalone, endpointOverrides } = options;

  if (standalone || !accountId) {
    // Standalone mode: use direct API client with the JWT token
    const token = await resolveToken(jwt);
    const auth = getAuth(token);
    return getApiEndpoints(auth, endpointOverrides);
  }

  // Extension mode: use background client that proxies through Chrome messaging
  return createBackgroundClient();
};

/**
 * Creates an API client with token and optional accountId providers.
 *
 * @param options - Configuration with getToken and optional getAccountId
 * @returns Object with refreshClient and getEndpoints methods
 *
 * @example
 * // Auth0 usage with token, accountId, and region-based endpoints
 * const client = apiClient({
 *   getToken: async () => getFreshAccessToken(),
 *   getAccountId: async () => getStoredAccountId(),
 *   getEndpointOverrides: (token) => getEndpointsForToken(token, AUTH0_CONFIG),
 * });
 *
 * @example
 * // Legacy usage (accountId extracted from JWT)
 * const client = apiClient({
 *   getToken: async () => getTokenFromStorage(),
 * });
 */
const apiClient = (options: ApiClientOptions) => {
  let endpoints = {} as ApiClient;

  const { getToken, getAccountId, getEndpointOverrides } = options;

  // This function creates new instance of API endpoints
  // For Auth0 mode (when getAccountId is provided), we pass the token provider function
  // so that each API request can get a fresh token
  const create = async () => {
    // Resolve token once per create() call
    const token = await resolveToken(getToken);

    let auth: AuthConfig;
    if (getAccountId) {
      // Auth0 mode: pass token provider function so each request gets a fresh token
      const accountId = await getAccountId();
      auth = { token: getToken, accountId };
    } else {
      // Legacy mode: use resolved token (accountId is extracted from JWT)
      auth = getAuth(token);
    }

    // Get endpoint overrides if provider is available
    const overrides = getEndpointOverrides
      ? await getEndpointOverrides(token)
      : {};

    endpoints = getApiEndpoints(auth, overrides);
  };

  // This function is used to refresh the token of the current instance of API endpoints
  // The token provider callback ensures we always get the fresh token
  const refreshClient = async () => {
    await create();
  };

  // This function is used to get the current instance of API endpoints
  const getEndpoints = async (): Promise<ApiClient> => {
    await create();

    return endpoints;
  };

  return {
    refreshClient,
    getEndpoints,
  };
};

export default apiClient;
